import type { RichEditorValue, RichAttachment } from '@/components/common/RichEditor';

/**
 * Description storage strategy:
 * - Backward compat: nếu description là text thuần cũ → wrap vào <p>
 * - Nếu bắt đầu bằng `{"__rich":true,...}` → parse JSON
 * - Mặc định: treat as HTML (nếu có tag) hoặc text thuần
 */

const RICH_MARKER = '__RICH_V1__';

function isLikelyHtml(s: string): boolean {
  return /<[a-z][^>]*>/i.test(s);
}

function escapeHtml(s: string): string {
  return s
    .replace(/&/g, '&amp;')
    .replace(/</g, '&lt;')
    .replace(/>/g, '&gt;');
}

function textToHtml(s: string): string {
  // Text thuần → <p> cho từng dòng
  if (!s.trim()) return '';
  return s
    .split(/\r?\n/)
    .map(line => `<p>${escapeHtml(line) || '<br>'}</p>`)
    .join('');
}

export function parseDescription(raw?: string | null): RichEditorValue {
  if (!raw) return { html: '', attachments: [] };

  // Check rich marker prefix
  if (raw.startsWith(RICH_MARKER)) {
    try {
      const json = raw.slice(RICH_MARKER.length);
      const obj = JSON.parse(json);
      return {
        html: String(obj.html ?? ''),
        attachments: Array.isArray(obj.attachments) ? obj.attachments : [],
      };
    } catch {
      return { html: raw, attachments: [] };
    }
  }

  // Legacy: HTML hoặc text thuần
  if (isLikelyHtml(raw)) {
    return { html: raw, attachments: [] };
  }
  return { html: textToHtml(raw), attachments: [] };
}

export function serializeDescription(value: RichEditorValue): string {
  const html = value.html ?? '';
  const atts = value.attachments ?? [];

  // Nếu không có attachments → lưu HTML thuần cho gọn
  if (atts.length === 0) {
    return html;
  }

  // Có attachments → lưu JSON với marker
  return RICH_MARKER + JSON.stringify({
    html,
    attachments: atts,
  });
}

/** Clean HTML trước khi render (basic sanitize). Production nên dùng DOMPurify. */
export function sanitizeHtml(html: string): string {
  // Loại bỏ script tags đơn giản. Production: dùng DOMPurify
  return html
    .replace(/<script\b[^<]*(?:(?!<\/script>)<[^<]*)*<\/script>/gi, '')
    .replace(/\son\w+\s*=\s*"[^"]*"/gi, '') // remove inline event handlers
    .replace(/\son\w+\s*=\s*'[^']*'/gi, '');
}

/** Convert empty HTML check — phát hiện editor rỗng */
export function isEmptyHtml(html?: string): boolean {
  if (!html) return true;
  const stripped = html.replace(/<[^>]+>/g, '').trim();
  return stripped.length === 0;
}

export type { RichEditorValue, RichAttachment };
